"use client";

import { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { FileManagerService } from "@/services/fileManagerService";
import {
  FileSystemItem,
  FileManagerState,
  CreateFolderRequest,
  AssetDetails,
} from "@/types/fileManager";

export interface UploadProgressInfo {
  fileName: string;
  fileSize: number;
  fileType: string;
  progress: number;
  status: "uploading" | "completed" | "error";
  error?: string;
  uploadId: string;
}

export const useFileManager = (initialPath: string = "") => {
  // Get auth state from Redux store
  const currentStoreInfo = useSelector(
    (state: RootState) => state.auth.currentStoreInfo,
  );

  // Initialize path with /store/storeType/storeId/ format if store info is available
  const getInitialPath = () => {
    if (currentStoreInfo?.storeId && currentStoreInfo?.storeType) {
      return `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets/`;
    }
    return initialPath || "";
  };

  const [state, setState] = useState<FileManagerState>({
    currentPath: getInitialPath(),
    items: [], // Always initialize as empty array
    selectedItems: [],
    loading: false,
    error: null,
    uploadProgress: {},
  });

  const [uploadProgressItems, setUploadProgressItems] = useState<
    UploadProgressInfo[]
  >([]);

  // Track virtual folders (client-side only folders that don't exist on server yet)
  const [virtualFolders, setVirtualFolders] = useState<Set<string>>(new Set());

  const addUploadProgressItem = useCallback((file: File) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newItem: UploadProgressInfo = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      progress: 0,
      status: "uploading",
      uploadId,
    };
    setUploadProgressItems((prev) => [...prev, newItem]);
    return uploadId;
  }, []);

  const updateUploadProgress = useCallback(
    (uploadId: string, progress: number) => {
      setUploadProgressItems((prev) =>
        prev.map((item) =>
          item.uploadId === uploadId
            ? { ...item, progress: Math.round(progress) }
            : item,
        ),
      );
    },
    [],
  );

  const updateUploadStatus = useCallback(
    (uploadId: string, status: "completed" | "error", error?: string) => {
      setUploadProgressItems((prev) =>
        prev.map((item) =>
          item.uploadId === uploadId
            ? {
                ...item,
                status,
                error,
                progress: status === "completed" ? 100 : item.progress,
              }
            : item,
        ),
      );
    },
    [],
  );

  const removeUploadProgressItem = useCallback((uploadId: string) => {
    setUploadProgressItems((prev) =>
      prev.filter((item) => item.uploadId !== uploadId),
    );
  }, []);

  const cancelUpload = useCallback(
    (uploadId: string) => {
      // Find the item and mark it as cancelled/error
      updateUploadStatus(uploadId, "error", "Upload cancelled");
      // Remove it after a brief delay
      setTimeout(() => removeUploadProgressItem(uploadId), 1000);
    },
    [updateUploadStatus, removeUploadProgressItem],
  );
  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setItems = useCallback((items: FileSystemItem[]) => {
    // Ensure items is always an array to prevent null/undefined errors
    setState((prev) => ({
      ...prev,
      items: Array.isArray(items) ? items : [],
    }));
  }, []);

  const setCurrentPath = useCallback((path: string) => {
    setState((prev) => ({ ...prev, currentPath: path }));
  }, []);

  const setSelectedItems = useCallback((items: FileSystemItem[]) => {
    // Ensure selectedItems is always an array
    setState((prev) => ({
      ...prev,
      selectedItems: Array.isArray(items) ? items : [],
    }));
  }, []);

  const setUploadProgress = useCallback(
    (filename: string, progress: number) => {
      setState((prev) => ({
        ...prev,
        uploadProgress: {
          ...prev.uploadProgress,
          [filename]: progress,
        },
      }));
    },
    [],
  );

  const removeUploadProgress = useCallback((filename: string) => {
    setState((prev) => {
      const newProgress = { ...prev.uploadProgress };
      delete newProgress[filename];
      return {
        ...prev,
        uploadProgress: newProgress,
      };
    });
  }, []);

  // Replace browseAssets with actual service call
  const browseAssets = useCallback(
    async (path?: string) => {
      setLoading(true);
      setError(null);

      try {
        if (!currentStoreInfo?.storeId || !currentStoreInfo?.storeType) {
          setError("Store information not available");
          return;
        }

        const basePath = `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets`;
        let targetPath = path || state.currentPath;

        // Ensure the target path is within the assets folder
        if (!targetPath.startsWith(basePath)) {
          console.warn("⚠️ Invalid target path, correcting to assets root:", {
            targetPath,
            basePath,
          });
          targetPath = basePath + "/";
        }

        console.log("📍 Browsing assets at path:", targetPath);
        const response = await FileManagerService.browseAssets(targetPath);

        if (response.valid && response.data) {
          let items = response.data.items || [];

          // Add virtual folders for the current path
          const virtualFoldersForPath = Array.from(virtualFolders)
            .filter((vPath) => {
              // Get parent path of virtual folder
              const parts = vPath.split("/").filter(Boolean);
              if (parts.length === 0) return false;
              const parentPath = "/" + parts.slice(0, -1).join("/") + "/";
              return parentPath === targetPath;
            })
            .map((vPath) => {
              const folderName = vPath.split("/").filter(Boolean).pop() || "";
              return {
                name: folderName,
                path: vPath,
                isFolder: true,
                size: 0,
                mimeType: "folder",
                assetId: "",
                url: "",
              } as FileSystemItem;
            });

          // Merge virtual folders with real items (avoiding duplicates)
          const existingPaths = new Set(items.map((item) => item.path));
          const newVirtualFolders = virtualFoldersForPath.filter(
            (vf) => !existingPaths.has(vf.path),
          );
          items = [...newVirtualFolders, ...items];

          setItems(items);

          // Validate server response path
          let serverPath = response.data.currentPath || targetPath;
          if (!serverPath.startsWith(basePath)) {
            console.warn(
              "⚠️ Server returned invalid path, using target path:",
              { serverPath, targetPath },
            );
            serverPath = targetPath;
          }

          setCurrentPath(serverPath);
        } else {
          setError("Failed to fetch assets");
        }
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to fetch assets",
        );
      } finally {
        setLoading(false);
      }
    },
    [state.currentPath, currentStoreInfo, virtualFolders], // Add virtualFolders dependency
  );

  const navigateToPath = useCallback(
    async (path: string) => {
      if (!currentStoreInfo?.storeId || !currentStoreInfo?.storeType) {
        console.error("❌ Cannot navigate: Missing store info");
        return;
      }

      const basePath = `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets`;

      // Validate that the path is within the allowed assets folder
      if (!path.startsWith(basePath)) {
        console.error("❌ Invalid path: Must be within assets folder", {
          path,
          basePath,
        });
        // Force navigation to the assets root
        path = basePath + "/";
      }

      console.log("📍 Navigating to path:", path);
      await browseAssets(path);
      setSelectedItems([]);
    },
    [browseAssets, currentStoreInfo], // Remove setter dependency
  );

  const navigateUp = useCallback(async () => {
    if (!currentStoreInfo?.storeId || !currentStoreInfo?.storeType) {
      console.error("❌ Cannot navigate up: Missing store info");
      return;
    }

    const basePath = `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets`;
    const pathParts = state.currentPath.split("/").filter(Boolean);

    // Don't allow navigation above the assets folder
    if (pathParts.length <= 4) {
      // ["store", storeType, storeId, "assets"]
      console.log("📍 Already at root assets folder, cannot navigate up");
      return;
    }

    pathParts.pop();
    const parentPath = "/" + pathParts.join("/") + "/";

    // Ensure we don't go above the assets folder
    if (!parentPath.startsWith(basePath)) {
      console.log("📍 Limiting navigation to assets folder");
      await navigateToPath(basePath + "/");
    } else {
      await navigateToPath(parentPath);
    }
  }, [state.currentPath, navigateToPath, currentStoreInfo]);

  const createFolder = useCallback(
    async (folderName: string) => {
      setLoading(true);
      setError(null);

      try {
        // Construct the full path for the new virtual folder
        const normalizedPath = state.currentPath.endsWith("/")
          ? state.currentPath
          : state.currentPath + "/";
        const newFolderPath = normalizedPath + folderName + "/";

        // Add to virtual folders set
        setVirtualFolders((prev) => new Set([...prev, newFolderPath]));

        // Create a virtual folder item
        const virtualFolderItem: FileSystemItem = {
          name: folderName,
          path: newFolderPath,
          isFolder: true,
          size: 0,
          mimeType: "folder",
          assetId: "",
          url: "",
        };

        // Add to items list immediately for instant feedback
        setState((prev) => ({
          ...prev,
          items: [virtualFolderItem, ...prev.items],
        }));

        // Show a warning to the user
        setError(
          `Folder "${folderName}" created. Note: This folder will only exist on the server after you upload files to it.`,
        );

        // Note: We don't call the API anymore as folders are client-side only
        await FileManagerService.createFolder({
          folderName,
          path: state.currentPath,
        });
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to create folder",
        );
      } finally {
        setLoading(false);
      }
    },
    [state.currentPath], // Remove browseAssets dependency to avoid refresh
  );

  // Upload file with real-time progress using XMLHttpRequest
  const uploadFileWithProgress = useCallback(
    async (file: File, uploadId: string): Promise<AssetDetails | null> => {
      if (!currentStoreInfo?.storeId || !currentStoreInfo?.storeType) {
        setError("Store information not available for upload");
        return null;
      }

      setError(null);

      try {
        // Validate upload path
        const basePath = `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets`;
        let uploadPath = state.currentPath;

        if (!uploadPath.startsWith(basePath)) {
          console.warn("⚠️ Invalid upload path, using assets root:", {
            uploadPath,
            basePath,
          });
          uploadPath = basePath + "/";
        }

        // Step 1: Get upload URL
        console.log("📤 Getting upload URL for path:", uploadPath);
        const { uploadUrl, assetId } = await FileManagerService.getUploadUrl(
          file.name,
          uploadPath,
        );

        updateUploadProgress(uploadId, 5); // 5% after getting URL

        // Step 2: Upload with real-time progress
        return new Promise<AssetDetails | null>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          // Track upload progress
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              // Scale progress from 5% to 95% (leaving 5% for completion)
              const scaledProgress = 5 + percentComplete * 0.9;
              console.log(
                `📊 Upload progress for ${file.name}: ${Math.round(scaledProgress)}%`,
              );
              updateUploadProgress(uploadId, scaledProgress);
            }
          });

          xhr.addEventListener("load", async () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              updateUploadProgress(uploadId, 95);

              try {
                // Step 3: Notify server that upload is complete
                console.log("📋 Completing asset upload:", assetId);
                const assetDetails =
                  await FileManagerService.completeAssetUpload(assetId);

                updateUploadProgress(uploadId, 100);
                updateUploadStatus(uploadId, "completed");

                console.log("✅ File upload successful:", assetDetails);

                // Remove any virtual folders that are now real (exist on server)
                setVirtualFolders((prev) => {
                  const newSet = new Set(prev);
                  // Remove virtual folders that are parent folders of the uploaded file
                  const uploadedPath = assetDetails.path || uploadPath;
                  const pathParts = uploadedPath.split("/").filter(Boolean);
                  for (let i = 1; i < pathParts.length; i++) {
                    const folderPath =
                      "/" + pathParts.slice(0, i).join("/") + "/";
                    newSet.delete(folderPath);
                  }
                  return newSet;
                });

                // Remove progress item after a delay
                setTimeout(() => removeUploadProgressItem(uploadId), 2000);

                // Refresh the directory
                browseAssets();

                resolve(assetDetails);
              } catch (error) {
                const errorMsg =
                  error instanceof Error
                    ? error.message
                    : "Failed to complete upload";
                console.error("❌ Complete upload failed:", errorMsg);
                updateUploadStatus(uploadId, "error", errorMsg);
                reject(new Error(errorMsg));
              }
            } else {
              const errorMsg = `Upload failed: ${xhr.statusText}`;
              console.error("❌ Upload failed:", errorMsg);
              updateUploadStatus(uploadId, "error", errorMsg);
              reject(new Error(errorMsg));
            }
          });

          xhr.addEventListener("error", () => {
            const errorMsg = "Network error during upload";
            console.error("❌ Upload error:", errorMsg);
            updateUploadStatus(uploadId, "error", errorMsg);
            reject(new Error(errorMsg));
          });

          xhr.addEventListener("abort", () => {
            const errorMsg = "Upload cancelled";
            console.log("⚠️ Upload aborted:", errorMsg);
            updateUploadStatus(uploadId, "error", errorMsg);
            reject(new Error(errorMsg));
          });

          // Start the upload
          xhr.open("PUT", uploadUrl);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.send(file);
        });
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Failed to upload file";
        updateUploadStatus(uploadId, "error", errorMsg);
        setError(errorMsg);
        return null;
      }
    },
    [
      state.currentPath,
      browseAssets, // Remove setter dependencies
      updateUploadProgress,
      updateUploadStatus,
      removeUploadProgressItem,
      currentStoreInfo,
    ],
  );

  const uploadFile = useCallback(
    async (
      file: File,
      onProgress?: (progress: number) => void,
    ): Promise<AssetDetails | null> => {
      // Add defensive checks
      if (!file) {
        console.error("❌ File is null or undefined");
        setError("File is required");
        return null;
      }

      if (!currentStoreInfo?.storeId || !currentStoreInfo?.storeType) {
        setError("Store information not available for upload");
        return null;
      }

      setError(null);

      try {
        // Validate upload path
        const basePath = `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets`;
        let uploadPath = state.currentPath;

        if (!uploadPath.startsWith(basePath)) {
          console.warn("⚠️ Invalid upload path, using assets root:", {
            uploadPath,
            basePath,
          });
          uploadPath = basePath + "/";
        }

        // Set initial progress
        setUploadProgress(file.name, 0);
        onProgress?.(0);

        // Use the service method directly to upload the file
        console.log("📤 Getting upload URL for path:", uploadPath);
        const { uploadUrl, assetId } = await FileManagerService.getUploadUrl(
          file.name,
          uploadPath,
        );

        // Progress update after getting URL
        setUploadProgress(file.name, 25);
        onProgress?.(25);

        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Failed to upload file: ${uploadResponse.statusText}`,
          );
        }

        // Progress update after successful upload
        setUploadProgress(file.name, 75);
        onProgress?.(75);

        // Step 3: Notify server that upload is complete
        console.log("📋 Completing asset upload:", assetId);
        const assetDetails =
          await FileManagerService.completeAssetUpload(assetId);

        // Progress update after completion
        setUploadProgress(file.name, 100);
        onProgress?.(100);

        console.log("✅ File upload successful:", assetDetails);

        // Remove any virtual folders that are now real (exist on server)
        setVirtualFolders((prev) => {
          const newSet = new Set(prev);
          // Remove virtual folders that are parent folders of the uploaded file
          const uploadedPath = assetDetails.path || uploadPath;
          const pathParts = uploadedPath.split("/").filter(Boolean);
          for (let i = 1; i < pathParts.length; i++) {
            const folderPath = "/" + pathParts.slice(0, i).join("/") + "/";
            newSet.delete(folderPath);
          }
          return newSet;
        });

        removeUploadProgress(file.name);
        await browseAssets(); // Refresh the current directory
        return assetDetails;
      } catch (error) {
        console.error("❌ useFileManager.uploadFile error:", error);
        removeUploadProgress(file.name);
        setError(
          error instanceof Error ? error.message : "Failed to upload file",
        );
        return null;
      }
    },
    [
      state.currentPath,
      browseAssets, // Remove setter dependencies
      currentStoreInfo,
    ],
  );

  const uploadMultipleFiles = useCallback(
    async (files: File[]): Promise<AssetDetails[]> => {
      const results: AssetDetails[] = [];
      const errors: string[] = [];

      // Create progress items for all files immediately
      const uploadIds = files.map((file) => addUploadProgressItem(file));

      // Process files concurrently for better UX
      const uploadPromises = files.map(async (file, index) => {
        const uploadId = uploadIds[index];
        try {
          const result = await uploadFileWithProgress(file, uploadId);

          if (result) {
            results.push(result);
            return { success: true, result, file: file.name };
          } else {
            errors.push(`${file.name}: Upload returned null`);
            return {
              success: false,
              error: "Upload returned null",
              file: file.name,
            };
          }
        } catch (error) {
          const errorMsg =
            error instanceof Error ? error.message : "Unknown error";
          errors.push(`${file.name}: ${errorMsg}`);
          return { success: false, error: errorMsg, file: file.name };
        }
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      if (errors.length > 0) {
        setError(
          `Some files failed to upload: ${errors.slice(0, 3).join(", ")}${errors.length > 3 ? "..." : ""}`,
        );
      }

      return results;
    },
    [addUploadProgressItem, uploadFileWithProgress, setError],
  );

  const deleteAsset = useCallback(
    async (item: FileSystemItem) => {
      setLoading(true);
      setError(null);

      try {
        await FileManagerService.deleteAsset(item.path);
        await browseAssets(); // Refresh the current directory
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to delete asset",
        );
      } finally {
        setLoading(false);
      }
    },
    [browseAssets], // Remove setter dependencies
  );

  const selectItem = useCallback(
    (item: FileSystemItem, multiSelect: boolean = false) => {
      if (!item) return; // Guard against null/undefined items

      if (multiSelect) {
        setState((prev) => {
          const isSelected = prev.selectedItems.some(
            (selected: FileSystemItem) => selected.path === item.path,
          );
          if (isSelected) {
            return {
              ...prev,
              selectedItems: prev.selectedItems.filter(
                (selected: FileSystemItem) => selected.path !== item.path,
              ),
            };
          } else {
            return {
              ...prev,
              selectedItems: [...prev.selectedItems, item],
            };
          }
        });
      } else {
        setSelectedItems([item]);
      }
    },
    [setSelectedItems],
  );

  const selectMultipleItems = useCallback(
    (items: FileSystemItem[]) => {
      setSelectedItems(Array.isArray(items) ? items : []);
    },
    [setSelectedItems],
  );

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, [setSelectedItems]);

  const isItemSelected = useCallback(
    (item: FileSystemItem) => {
      if (!item || !state.selectedItems) return false;
      return state.selectedItems.some(
        (selected) => selected?.path === item.path,
      );
    },
    [state.selectedItems],
  );

  // Update current path when store info changes
  useEffect(() => {
    if (currentStoreInfo?.storeId && currentStoreInfo?.storeType) {
      const newPath = `/store/${currentStoreInfo.storeType}/${currentStoreInfo.storeId}/assets/`;
      if (state.currentPath !== newPath) {
        setCurrentPath(newPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreInfo?.storeId, currentStoreInfo?.storeType]); // Only depend on the actual values, not the setters

  // Initialize with browsing the initial path
  useEffect(() => {
    browseAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return {
    // State - use local state instead of Redux state
    currentPath: state.currentPath,
    items: state.items,
    selectedItems: state.selectedItems,
    loading: state.loading,
    error: state.error,
    uploadProgress: state.uploadProgress,
    uploadProgressItems,

    // Actions
    browseAssets,
    navigateToPath,
    navigateUp,
    createFolder,
    uploadFile,
    uploadMultipleFiles,
    deleteAsset,
    selectItem,
    selectMultipleItems,
    clearSelection,
    isItemSelected,

    // Upload progress management
    addUploadProgressItem,
    updateUploadProgress,
    updateUploadStatus,
    removeUploadProgressItem,
    cancelUpload,

    // Utilities
    setError,
    setLoading,
  };
};
